type Env = Record<string, string | undefined>;

const env: Env =
  (import.meta as ImportMeta & { env?: Env }).env ??
  (globalThis as { process?: { env: Env } }).process?.env ??
  {};

export const DOMAINS = {
  root: env.VITE_ROOT_DOMAIN || "sp4r0ww.com",
  static: env.VITE_STATIC_DOMAIN || "static.sp4r0ww.com",
  legal: env.VITE_LEGAL_DOMAIN || "legal.sp4r0ww.com",
  legacy: (env.VITE_LEGACY_DOMAINS ?? "sp4r0ww.online").split(",").map((d) => d.trim()).filter(Boolean),
};

export const SITE = {
  main: `https://${DOMAINS.root}`,
  lab: `https://lab.${DOMAINS.root}`,
  hideout: `https://hideout.${DOMAINS.root}`,
  legal: `https://${DOMAINS.legal}`,
  static: `https://${DOMAINS.static}`,
};

export const LAB_HOST = `lab.${DOMAINS.root}`;

type SiteKey = keyof typeof SITE;

const LINK_TOKEN = /^@(\w+)(\/\S*)?$/;
const MD_LINK_TOKEN = /\]\(@(\w+)(\/[^)\s]*)?\)/g;
const OWN_URL = new RegExp(
  `https?://(?:(lab|hideout|static|legal|www)\\.)?(?:${[DOMAINS.root, ...DOMAINS.legacy]
    .map((d) => d.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|")})(?=[/?#"'\\s)]|$)`,
  "g"
);

const isSiteKey = (key: string): key is SiteKey => key in SITE;
const siteKeyOf = (sub: string | undefined): SiteKey => (sub && sub !== "www" && isSiteKey(sub) ? sub : "main");

export function resolveLink(value: string): string {
  const token = LINK_TOKEN.exec(value);
  if (token && isSiteKey(token[1])) return SITE[token[1]] + (token[2] ?? "");
  return value
    .replace(MD_LINK_TOKEN, (match, key: string, path?: string) =>
      isSiteKey(key) ? `](${SITE[key]}${path ?? ""})` : match
    )
    .replace(OWN_URL, (_, sub?: string) => SITE[siteKeyOf(sub)]);
}

export function resolveLinks<T>(value: T): T {
  if (typeof value === "string") return resolveLink(value) as T;
  if (Array.isArray(value)) return value.map(resolveLinks) as T;
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, resolveLinks(item)])) as T;
  }
  return value;
}

export function toLinkToken(value: string): string {
  const match = new RegExp(`^${OWN_URL.source}(\\S*)$`).exec(value);
  return match ? `@${siteKeyOf(match[1])}${match[2]}` : value;
}
